import React, { useEffect, useState } from "react";
import { CurrencyDollarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import CustomTableComponent from "../../components/CustomTableComponent";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const formatMoney = (n) =>
  n ? Number(n).toLocaleString("vi-VN") + " ₫" : "0 ₫";

const MyTransactionModal = ({
  isOpen,
  onClose,
  filterStart,
  filterEnd,
  setFilterStart,
  setFilterEnd,
  loading,
  chartData,
  totalValue,
  orderList,
  tableLoading,
  companyName,
  hasContactPriceOrder,
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white w-full max-w-5xl mx-4 rounded-xl p-6 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
            <div>
              <h2 className="text-xl font-bold  mb-1">
                Thống kê giao dịch:{" "}
                <span className="text-emerald-700">
                  {companyName || "Đơn vị bạn"}
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="font-medium text-gray-500 text-sm">Từ</span>
                <input
                  type="date"
                  value={filterStart}
                  max={filterEnd}
                  onChange={(e) => setFilterStart(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm text-gray-600 bg-gray-50"
                />
                <span className="font-medium text-gray-500 text-sm">đến</span>
                <input
                  type="date"
                  value={filterEnd}
                  min={filterStart}
                  max={dayjs().format("YYYY-MM-DD")}
                  onChange={(e) => setFilterEnd(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm text-gray-600 bg-gray-50"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 md:mt-0">
              <span className="font-medium text-gray-500">Tổng giá trị:</span>
              <span className="font-bold text-emerald-700 text-xl">
                {loading
                  ? "..."
                  : hasContactPriceOrder
                    ? "Đang cập nhật"
                    : formatMoney(totalValue)}
              </span>
            </div>
          </div>

          <div className="mb-8">
            <div className="font-semibold text-emerald-600 mb-2 flex items-center gap-2">
              <CurrencyDollarIcon className="h-6 w-6" /> Giá trị giao dịch theo
              thời gian
            </div>
            <div className="bg-gray-50 rounded-xl border px-2 py-3">
              {loading ? (
                <div className="text-gray-400 text-center py-8">
                  Đang tải dữ liệu...
                </div>
              ) : chartData.length === 0 ? (
                <div className="text-gray-400 text-center py-8">
                  Không có dữ liệu
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={chartData}>
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={formatMoney} width={90} />
                    <Tooltip formatter={formatMoney} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Tổng giá trị"
                      stroke="#34d399"
                      fill="#bbf7d0"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div>
            <div className="font-semibold mb-3 text-blue-700 flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5" /> Danh sách đơn hàng
            </div>
            {tableLoading ? (
              <div className="text-center py-6 text-gray-400">Đang tải...</div>
            ) : (
              <CustomTableComponent
                headers={[
                  {
                    key: "createdAt",
                    label: "Ngày tạo",
                    render: (o) =>
                      dayjs(o.createdAt).format("DD/MM/YYYY HH:mm"),
                  },
                  {
                    key: "totalPrice",
                    label: "Tổng giá trị",
                    render: (o) => (
                      <span className="font-semibold text-emerald-700">
                        {Number(o.totalPrice) === 0
                          ? "Giá liên hệ"
                          : formatMoney(o.totalPrice)}
                      </span>
                    ),
                  },
                ]}
                data={orderList}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const MyTransactionStatisticsCard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [cardTotalValue, setCardTotalValue] = useState(0);
  const [cardLoading, setCardLoading] = useState(true);
  const [cardHasContactPriceOrder, setCardHasContactPriceOrder] =
    useState(false);
  const [filterStart, setFilterStart] = useState(
    dayjs().startOf("year").format("YYYY-MM-DD"),
  );
  const [filterEnd, setFilterEnd] = useState(dayjs().format("YYYY-MM-DD"));
  const [modalTotalValue, setModalTotalValue] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [modalLoading, setModalLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [modalHasContactPriceOrder, setModalHasContactPriceOrder] =
    useState(false);
  const companyId = localStorage.getItem("companyId");

  useEffect(() => {
    if (!companyId) return;
    let ignore = false;
    async function fetchCardTotal() {
      setCardLoading(true);
      try {
        const res = await ApiCustom.get("/order-show");
        let orders = res?.data?.data?.order || [];
        orders = orders.filter(
          (o) =>
            ["success", "approved", "Đã duyệt"].includes(o.status) &&
            dayjs(o.createdAt).format("YYYY-MM-DD") >=
              dayjs().startOf("year").format("YYYY-MM-DD") &&
            dayjs(o.createdAt).format("YYYY-MM-DD") <=
              dayjs().format("YYYY-MM-DD"),
        );
        const ordersWithDetails = await Promise.all(
          orders.map((order) =>
            ApiCustom.get(`/order-show-id/${order.id}`)
              .then((detailRes) => ({
                ...order,
                details: detailRes?.data?.data?.details || [],
              }))
              .catch(() => ({
                ...order,
                details: [],
              })),
          ),
        );
        const myOrders = ordersWithDetails.filter(
          (order) =>
            order.details.length > 0 &&
            String(order.details[0].product?.companyId) === String(companyId),
        );
        let total = 0;
        let hasContactPriceOrder = false;
        for (const order of myOrders) {
          total += Number(order.totalPrice || 0);
          if (
            order.details?.some((detail) => detail?.product?.isContactPrice) ||
            Number(order.totalPrice || 0) === 0
          ) {
            hasContactPriceOrder = true;
          }
        }
        if (!ignore) {
          setCardTotalValue(total);
          setCardHasContactPriceOrder(hasContactPriceOrder);
        }
      } catch {
        if (!ignore) {
          setCardTotalValue(0);
          setCardHasContactPriceOrder(false);
        }
      } finally {
        if (!ignore) setCardLoading(false);
      }
    }
    fetchCardTotal();
    return () => {
      ignore = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!isOpen || !companyId) return;
    let ignore = false;
    async function fetchMyStats() {
      setModalLoading(true);
      setTableLoading(true);
      setModalTotalValue(0);
      setChartData([]);
      setOrderList([]);
      setModalHasContactPriceOrder(false);
      try {
        const res = await ApiCustom.get("/order-show");
        let orders = res?.data?.data?.order || [];
        orders = orders.filter(
          (o) =>
            ["success", "approved", "Đã duyệt"].includes(o.status) &&
            dayjs(o.createdAt).format("YYYY-MM-DD") >= filterStart &&
            dayjs(o.createdAt).format("YYYY-MM-DD") <= filterEnd,
        );
        const ordersWithDetails = await Promise.all(
          orders.map((order) =>
            ApiCustom.get(`/order-show-id/${order.id}`)
              .then((detailRes) => ({
                ...order,
                details: detailRes?.data?.data?.details || [],
              }))
              .catch(() => ({
                ...order,
                details: [],
              })),
          ),
        );
        const myOrders = ordersWithDetails.filter(
          (order) =>
            order.details.length > 0 &&
            String(order.details[0].product?.companyId) === String(companyId),
        );

        if (!companyName && myOrders[0]?.details[0]?.product?.companyId) {
          try {
            const compRes = await ApiCustom.get(
              `/company-show-by-id/${companyId}`,
            );
            setCompanyName(compRes?.data?.data?.companyName || "");
          } catch {}
        }

        let total = 0;
        let hasContactPriceOrder = false;
        const chartObj = {};
        for (const order of myOrders) {
          const d = dayjs(order.createdAt).format("YYYY-MM-DD");
          chartObj[d] = (chartObj[d] || 0) + Number(order.totalPrice || 0);
          total += Number(order.totalPrice || 0);
          if (
            order.details?.some((detail) => detail?.product?.isContactPrice) ||
            Number(order.totalPrice || 0) === 0
          ) {
            hasContactPriceOrder = true;
          }
        }
        const chartArr = Object.entries(chartObj)
          .map(([date, value]) => ({ date, value }))
          .sort((a, b) => a.date.localeCompare(b.date));
        if (!ignore) {
          setModalTotalValue(total);
          setChartData(chartArr);
          setOrderList(myOrders);
          setModalHasContactPriceOrder(hasContactPriceOrder);
        }
      } catch {
        if (!ignore) {
          setModalTotalValue(0);
          setChartData([]);
          setOrderList([]);
          setModalHasContactPriceOrder(false);
        }
      } finally {
        if (!ignore) {
          setModalLoading(false);
          setTableLoading(false);
        }
      }
    }
    fetchMyStats();
    return () => {
      ignore = true;
    };
  }, [isOpen, filterStart, filterEnd, companyId]);

  return (
    <div className="w-full">
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full h-full sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-emerald-100 transition-all cursor-pointer shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-emerald-100 rounded-full mb-3 flex items-center justify-center">
          <CurrencyDollarIcon className="h-6 w-6 text-emerald-600" />
        </div>
        <p className="text-lg text-black mb-1">
          Giá trị giao dịch của đơn vị bạn
        </p>
        <p className="text-3xl font-bold ">
          {cardLoading
            ? "..."
            : cardHasContactPriceOrder
              ? "Đang cập nhật"
              : formatMoney(cardTotalValue)}
        </p>
      </motion.div>

      <MyTransactionModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        filterStart={filterStart}
        filterEnd={filterEnd}
        setFilterStart={setFilterStart}
        setFilterEnd={setFilterEnd}
        loading={modalLoading}
        chartData={chartData}
        totalValue={modalTotalValue}
        orderList={orderList}
        tableLoading={tableLoading}
        companyName={companyName}
        hasContactPriceOrder={modalHasContactPriceOrder}
      />
    </div>
  );
};

export default MyTransactionStatisticsCard;
